import React, { useRef, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';
import { applicationsApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';
import { useTabStore } from '../store/tabStore';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const LOADING_MSG = '正在处理中...';

const ApplicationView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { theme } = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const token = localStorage.getItem('token') || '';
  const [appId, setAppId] = useState<number | null>(null);
  const staticUrl = appId ? `${API_BASE}/sub_app/${appId}/static/index.html?theme=${theme}` : '';
  const { addTab } = useTabStore();
  const navigate = useNavigate();
  const [appName, setAppName] = useState('');
  const [appIcon, setAppIcon] = useState('');
  const [pluginLoading, setPluginLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('正在处理中...');
  const [thinkingText, setThinkingText] = useState('');
  const [toolCallText, setToolCallText] = useState('');
  const [dotCount, setDotCount] = useState(1);

  // Add tab with real app name; same name → same tab key (no duplicate)
  useEffect(() => {
    if (!slug) return;
    const fetchApp = /^\d+$/.test(slug) ? applicationsApi.get(Number(slug)) : applicationsApi.getBySlug(slug);
    fetchApp.then((res: any) => {
      const name = res.name || '应用访问';
      setAppName(name);
      setAppIcon(res.icon || '');
      setAppId(res.id);
      addTab({ key: `app-view:${name}`, path: `/app/${slug}/view`, label: name, closable: true });
    }).catch(() => {
      addTab({ key: `/app/${slug}/view`, path: `/app/${slug}/view`, label: '应用访问', closable: true });
    });
  }, [slug]);

  // Strip apple-content styles for full-bleed iframe — handled via CSS :has()

  useEffect(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'theme-change', theme }, '*');
    }
  }, [theme]);

  // Inject auth token into iframe via postMessage after load
  const handleIframeLoad = () => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'auth-token', token }, '*');
  };

  // Listen to plugin loading events — only respond to messages from THIS iframe
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === 'plugin-loading') {
        if (e.data.action === 'show') {
          setPluginLoading(true);
          setLoadingMessage(e.data.message || '正在处理中...');
          setThinkingText('');
          setToolCallText('');
          setDotCount(1);
        } else if (e.data.action === 'update') {
          const msg = e.data.message || '';
          setLoadingMessage(msg);
          // 思考过程累加，工具调用替换
          if (msg.startsWith('💭')) {
            const content = msg.replace('💭 ', '');
            setThinkingText(prev => prev + content);
          } else if (msg.startsWith('🔧')) {
            setToolCallText(msg.replace('🔧 ', ''));
          }
        } else if (e.data.action === 'hide') {
          setPluginLoading(false);
          setThinkingText('');
          setToolCallText('');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Cycle dots every 500ms
  useEffect(() => {
    if (!pluginLoading) return;
    const dotTimer = setInterval(() => setDotCount(d => d >= 3 ? 1 : d + 1), 500);
    return () => clearInterval(dotTimer);
  }, [pluginLoading]);

  return (
    <div className="application-view" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, position: 'relative' }}>
      {/* Plugin title bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 16px',
        height: 44,
        flexShrink: 0,
        borderBottom: '1px solid var(--apple-border)',
        background: 'var(--apple-bg-secondary)',
      }}>
        <Tooltip title="返回应用列表">
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/applications')}
            style={{ color: 'var(--apple-text-secondary)' }}
          />
        </Tooltip>
        <span style={{ color: 'var(--apple-text-secondary)', fontSize: 13 }}>应用</span>
        <span style={{ color: 'var(--apple-text-secondary)', fontSize: 13 }}>/</span>
        {appIcon && !appIcon.startsWith('http') && !appIcon.startsWith('data:') && !appIcon.startsWith('/uploads/') && !appIcon.startsWith('/avatars/') && !appIcon.startsWith('/app-icons/') && (
          <span style={{ fontSize: 16, lineHeight: 1 }}>{appIcon}</span>
        )}
        {appIcon && (appIcon.startsWith('http') || appIcon.startsWith('data:') || appIcon.startsWith('/uploads/') || appIcon.startsWith('/avatars/') || appIcon.startsWith('/app-icons/')) && (
          <img src={resolveUrl(appIcon)} alt="" style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 4 }} />
        )}
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--apple-text-primary)' }}>{appName}</span>
        <div style={{ flex: 1 }} />
      </div>
      {staticUrl && (
        <iframe
          ref={iframeRef}
          src={staticUrl}
          onLoad={handleIframeLoad}
          style={{ width: '100%', flex: 1, border: 'none', display: 'block' }}
          title="应用页面"
        />
      )}
      {/* Platform-level loading overlay */}
      {pluginLoading && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(10,12,20,0.75)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          {/* Main card 650x500 */}
          <div style={{
            width: 650,
            height: 500,
            background: 'rgba(30,30,45,0.95)',
            border: '1px solid rgba(99,179,237,0.2)',
            borderRadius: 16,
            backdropFilter: 'blur(12px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Header with loading animation */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid rgba(99,179,237,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              {/* Spinning dot */}
              <div style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: '2px solid rgba(99,179,237,0.3)',
                borderTopColor: '#63b3ed',
                animation: 'spin 1s linear infinite',
              }} />
              <span style={{
                color: 'rgba(147,197,253,0.95)',
                fontSize: 14,
                fontWeight: 500,
              }}>思考中...</span>
            </div>
            
            {/* Thinking content */}
            <div style={{
              flex: 1,
              padding: '20px',
              overflowY: 'auto',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 13,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
            ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
              {thinkingText || '准备中...'}
            </div>
            
            {/* Tool execution footer */}
            {toolCallText && (
              <div style={{
                padding: '14px 20px',
                borderTop: '1px solid rgba(99,179,237,0.15)',
                color: 'rgba(147,197,253,0.9)',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ opacity: 0.7, flexShrink: 0 }}>工具执行:</span>
                <span style={{ 
                  flex: 1, 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{toolCallText}</span>
              </div>
            )}
          </div>
          <style>{`
            @keyframes ai-ping {
              0% { transform: scale(1); opacity: 0.6; }
              100% { transform: scale(1.8); opacity: 0; }
            }
            @keyframes ai-pulse {
              0%, 100% { opacity: 0.4; }
              50% { opacity: 1; }
            }
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(-10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default ApplicationView;
